import { t as _objectSpread2 } from "./objectSpread2-BNmiQYyW.js";
import { n as SearchIcon, t as _objectWithoutProperties } from "./objectWithoutProperties-phqYxDS0.js";
import { a as AlgoliaLogo, c as SourceIcon, d as _objectDestructuringEmpty, f as RecentIcon, i as manageLocalStorageQuota, l as CloseIcon, m as LoadingIcon, p as SparklesIcon, r as createStoredSearches, u as _extends } from "./useSearchClient-mIHVB6ga.js";
import React, { createElement, useMemo, useState } from "react";
import { useTheme } from "@docsearch/core/useTheme";
import { Menu } from "@base-ui/react/menu";

//#region src/utils/capitalize.ts
function capitalize(text) {
	return `${text.charAt(0).toLocaleUpperCase()}${text.substring(1)}`;
}

//#endregion
//#region src/utils/collections.ts
const SOURCE_IDS = {
	askAI: "askAI",
	favoriteSearches: "favoriteSearches",
	recentConversations: "recentConversations",
	recentSearches: "recentSearches"
};
function getCollection(state, sourceId) {
	return state.collections.find((collection) => (collection === null || collection === void 0 ? void 0 : collection.source.sourceId) === sourceId);
}

//#endregion
//#region src/utils/decodeHtmlEntities.ts
function decodeHtmlEntities(text) {
	return text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#039;/g, "'").replace(/&amp;/g, "&");
}

//#endregion
//#region src/utils/getHitItemBreadcrumbs.ts
const LEVELS = [
	"lvl0",
	"lvl1",
	"lvl2",
	"lvl3",
	"lvl4",
	"lvl5",
	"lvl6"
];
function getHitItemBreadcrumbs(item) {
	const currentIndex = item.type === "content" || item.type === "askAI" ? LEVELS.length : LEVELS.indexOf(item.type);
	return LEVELS.slice(0, currentIndex).map((lvl) => item.hierarchy[lvl] ? decodeHtmlEntities(item.hierarchy[lvl]) : null).filter(Boolean).join(" > ");
}

//#endregion
//#region src/utils/getNestedValue.ts
const segmentsCache = /* @__PURE__ */ new Map();
function parseKey(key) {
	const cachedKey = segmentsCache.get(key);
	if (cachedKey) return cachedKey;
	const segments = key.replace(/\[(\w+)\]/g, ".$1").replace(/^\/./, "").split(".");
	segmentsCache.set(key, segments);
	return segments;
}
function getNestedValue(obj, key) {
	return parseKey(key).reduce((acc, curr) => {
		if (acc && typeof acc === "object" && Object.prototype.hasOwnProperty.call(acc, curr)) return acc[curr];
	}, obj);
}

//#endregion
//#region src/utils/groupBy.ts
function groupBy(values, predicate, maxResultsPerGroup) {
	return values.reduce((acc, item) => {
		const key = predicate(item);
		if (!acc.hasOwnProperty(key)) acc[key] = [];
		if (acc[key].length < (maxResultsPerGroup || 5)) acc[key].push(item);
		return acc;
	}, {});
}

//#endregion
//#region src/utils/identity.ts
function identity(x) {
	return x;
}

//#endregion
//#region src/utils/isModifierEvent.ts
/**
* Detect when an event is modified with a special key to let the browser
* trigger its default behavior.
*/
function isModifierEvent(event) {
	return event.button === 1 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

//#endregion
//#region src/utils/isQueryEmpty.ts
function isQueryEmpty(query) {
	if (!query || query.trim().length === 0) return true;
	return false;
}

//#endregion
//#region src/utils/noop.ts
function noop(..._args) {}

//#endregion
//#region src/utils/removeHighlightTags.ts
const regexHighlightTags = /(<mark>|<\/mark>)/g;
const regexHasHighlightTags = RegExp(regexHighlightTags.source);
function removeHighlightTags(hit) {
	var _internalDocSearchHit, _hit$_highlightResult;
	const internalDocSearchHit = hit;
	if (!internalDocSearchHit.__docsearch_parent && !hit._highlightResult) return hit.hierarchy.lvl0;
	const lvl0 = internalDocSearchHit.__docsearch_parent ? (_internalDocSearchHit = internalDocSearchHit.__docsearch_parent) === null || _internalDocSearchHit === void 0 || (_internalDocSearchHit = _internalDocSearchHit._highlightResult) === null || _internalDocSearchHit === void 0 || (_internalDocSearchHit = _internalDocSearchHit.hierarchy) === null || _internalDocSearchHit === void 0 ? void 0 : _internalDocSearchHit.lvl0 : (_hit$_highlightResult = hit._highlightResult) === null || _hit$_highlightResult === void 0 || (_hit$_highlightResult = _hit$_highlightResult.hierarchy) === null || _hit$_highlightResult === void 0 ? void 0 : _hit$_highlightResult.lvl0;
	if (!lvl0) return hit.hierarchy.lvl0;
	return lvl0.value && regexHasHighlightTags.test(lvl0.value) ? lvl0.value.replace(regexHighlightTags, "") : lvl0.value;
}

//#endregion
//#region src/utils/scrollTo.ts
function scrollTo(el) {
	if (typeof el.scrollTo === "function") el.scrollTo({
		top: 0,
		behavior: "smooth"
	});
	else el.scrollTop = 0;
}

//#endregion
//#region src/icons/SelectIcon.tsx
function SelectIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		className: "DocSearch-Hit-Select-Icon",
		width: "20",
		height: "20",
		viewBox: "0 0 20 20"
	}, /* @__PURE__ */ React.createElement("g", {
		stroke: "currentColor",
		fill: "none",
		fillRule: "evenodd",
		strokeLinecap: "round",
		strokeLinejoin: "round"
	}, /* @__PURE__ */ React.createElement("path", { d: "M18 3v4c0 2-2 4-4 4H2" }), /* @__PURE__ */ React.createElement("path", { d: "M8 17l-6-6 6-6" })));
}

//#endregion
//#region src/icons/ErrorIcon.tsx
function ErrorIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		width: "40",
		height: "40",
		viewBox: "0 0 20 20",
		fill: "none",
		fillRule: "evenodd",
		stroke: "currentColor",
		strokeLinecap: "round",
		strokeLinejoin: "round"
	}, /* @__PURE__ */ React.createElement("path", { d: "M19 4.8a16 16 0 00-2-1.2m-3.3-1.2A16 16 0 001.1 4.7M16.7 8a12 12 0 00-2.8-1.4M10 6a12 12 0 00-6.7 2M12.3 14.7a4 4 0 00-4.5 0M14.5 11.4A8 8 0 0010 10M3 16L18 2M10 18h0" }));
}

//#endregion
//#region src/icons/NoResultsIcon.tsx
function NoResultsIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		width: "64",
		height: "64",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "#5a5e9a",
		strokeWidth: "1.5",
		strokeLinecap: "round",
		strokeLinejoin: "round"
	}, /* @__PURE__ */ React.createElement("path", { d: "m13.5 8.5-5 5" }), /* @__PURE__ */ React.createElement("path", { d: "m8.5 8.5 5 5" }), /* @__PURE__ */ React.createElement("circle", {
		cx: "11",
		cy: "11",
		r: "8"
	}), /* @__PURE__ */ React.createElement("path", { d: "m21 21-4.3-4.3" }));
}

//#endregion
//#region src/icons/PinIcon.tsx
function PinIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		width: "24",
		height: "24",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round"
	}, /* @__PURE__ */ React.createElement("path", { d: "M12 17v5" }), /* @__PURE__ */ React.createElement("path", { d: "M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" }));
}

//#endregion
//#region src/icons/ChevronIcon.tsx
function ChevronIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		width: "24",
		height: "24",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		"aria-hidden": "true"
	}, /* @__PURE__ */ React.createElement("polyline", { points: "6 9 12 15 18 9" }));
}

//#endregion
//#region src/icons/CheckIcon.tsx
function CheckIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		width: "24",
		height: "24",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		"aria-hidden": "true"
	}, /* @__PURE__ */ React.createElement("polyline", { points: "20 6 9 17 4 12" }));
}

//#endregion
//#region src/utils/facets.ts
const MAX_FACETS = 5;
function normalizeFacets(facets = []) {
	const facetsMap = /* @__PURE__ */ new Map();
	for (const facet of facets) {
		const key = facet.key.trim().toLowerCase();
		if (key.length === 0 || facetsMap.has(key)) continue;
		if (facetsMap.size >= 5) break;
		facetsMap.set(key, facet);
	}
	return Array.from(facetsMap.values());
}
function getFacetLabel(facet) {
	if (facet.label) return facet.label;
	return facet.key.replace(/[._-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
/**
* Extracts the facet key from an Algolia `facetFilters` string entry such as
* `language:en`. Returns `null` when the entry doesn't have a `key:value`
* shape.
*/
function getFacetFilterKey(facetFilter) {
	const separatorIndex = facetFilter.indexOf(":");
	return separatorIndex <= 0 ? null : facetFilter.slice(0, separatorIndex);
}
function parseFacetFilter(facetFilter) {
	const key = getFacetFilterKey(facetFilter);
	if (key === null) return null;
	const value = facetFilter.slice(key.length + 1);
	return value ? {
		key,
		value
	} : null;
}
/**
* Reads the initial facet selections from the configured `facetFilters` of each
* index. Later indices override earlier ones for the same facet key.
*
* - A `key:value` string selects a single value.
* - A nested OR group whose entries all share the same key (for example
*   `['docusaurus_tag:default', 'docusaurus_tag:docs-default-current']`)
*   selects every value in the group.
* - OR groups that mix facet keys can't map to a single facet and are ignored.
*/
function deriveDefaultSelectedFacetsFromIndex(indices) {
	const defaultFacets = {};
	for (const index of indices) {
		var _index$searchParamete;
		const facetFilters = (_index$searchParamete = index.searchParameters) === null || _index$searchParamete === void 0 ? void 0 : _index$searchParamete.facetFilters;
		for (const facetFilter of Array.isArray(facetFilters) ? facetFilters : [facetFilters]) {
			if (typeof facetFilter === "string") {
				const parsed = parseFacetFilter(facetFilter);
				if (parsed) defaultFacets[parsed.key] = [parsed.value];
				continue;
			}
			if (!Array.isArray(facetFilter) || facetFilter.length === 0) continue;
			let groupKey = null;
			const groupValues = [];
			for (const entry of facetFilter) {
				const parsed = typeof entry === "string" ? parseFacetFilter(entry) : null;
				if (!parsed || groupKey !== null && parsed.key !== groupKey) {
					groupKey = null;
					break;
				}
				groupKey = parsed.key;
				groupValues.push(parsed.value);
			}
			if (groupKey !== null) defaultFacets[groupKey] = groupValues;
		}
	}
	return defaultFacets;
}

//#endregion
//#region src/components/ui/Chip.tsx
const _excluded$6 = ["className", "type"];
function Chip({ children }) {
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Chip" }, children);
}
function ChipDismiss(_ref) {
	let { className, type = "button" } = _ref, props = _objectWithoutProperties(_ref, _excluded$6);
	const cn = `DocSearch-Chip-Dismiss${className ? ` ${className}` : ""}`;
	return /* @__PURE__ */ React.createElement("button", _objectSpread2({
		type,
		className: cn,
		tabIndex: 0
	}, props), /* @__PURE__ */ React.createElement(CloseIcon, { "aria-hidden": "true" }));
}
Chip.Dismiss = ChipDismiss;

//#endregion
//#region src/components/ui/Menu.tsx
const _excluded$5 = ["className"];
const _excluded2 = [
	"align",
	"alignOffset",
	"side",
	"sideOffset",
	"collisionBoundary",
	"container"
];
const _excluded3 = ["className"];
const _excluded4 = ["className", "children"];
const _excluded5 = ["className", "children"];
const _excluded6 = ["className"];
function Menu$1(_ref) {
	let props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
	return /* @__PURE__ */ React.createElement(Menu.Root, props);
}
Menu$1.Trigger = React.forwardRef((_ref2, ref) => {
	let { className } = _ref2, props = _objectWithoutProperties(_ref2, _excluded$5);
	const cn = `DocSearch-Menu-Trigger${className ? ` ${className}` : ""}`;
	return /* @__PURE__ */ React.createElement(Menu.Trigger, _objectSpread2({
		className: cn,
		ref
	}, props));
});
function MenuPopup(_ref3) {
	let { align = "start", alignOffset = 0, side = "bottom", sideOffset = 4, collisionBoundary = "clipping-ancestors", container } = _ref3, props = _objectWithoutProperties(_ref3, _excluded2);
	return /* @__PURE__ */ React.createElement(Menu.Portal, { container }, /* @__PURE__ */ React.createElement(Menu.Positioner, {
		className: "DocSearch-Menu-Positioner",
		align,
		alignOffset,
		side,
		sideOffset,
		collisionBoundary
	}, /* @__PURE__ */ React.createElement(Menu.Popup, _objectSpread2({ className: "DocSearch-Menu-Popup" }, props))));
}
Menu$1.Popup = MenuPopup;
function MenuItem(_ref4) {
	let { className } = _ref4, props = _objectWithoutProperties(_ref4, _excluded3);
	const cn = `DocSearch-Menu-Item${className ? ` ${className}` : ""}`;
	return /* @__PURE__ */ React.createElement(Menu.Item, _objectSpread2({ className: cn }, props));
}
Menu$1.Item = MenuItem;
function MenuRadioGroup(_ref5) {
	let props = _extends({}, (_objectDestructuringEmpty(_ref5), _ref5));
	return /* @__PURE__ */ React.createElement(Menu.RadioGroup, props);
}
Menu$1.RadioGroup = MenuRadioGroup;
function MenuRadioItem(_ref6) {
	let { className, children } = _ref6, props = _objectWithoutProperties(_ref6, _excluded4);
	const cn = `DocSearch-Menu-Item DocSearch-Menu-RadioItem${className ? ` ${className}` : ""}`;
	return /* @__PURE__ */ React.createElement(Menu.RadioItem, _objectSpread2({
		className: cn,
		closeOnClick: true
	}, props), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Menu-RadioItem-Indicator" }, /* @__PURE__ */ React.createElement(Menu.RadioItemIndicator, null, /* @__PURE__ */ React.createElement(CheckIcon, null))), children);
}
Menu$1.RadioItem = MenuRadioItem;
function MenuCheckboxItem(_ref7) {
	let { className, children } = _ref7, props = _objectWithoutProperties(_ref7, _excluded5);
	const cn = `DocSearch-Menu-Item DocSearch-Menu-CheckboxItem${className ? ` ${className}` : ""}`;
	return /* @__PURE__ */ React.createElement(Menu.CheckboxItem, _objectSpread2({ className: cn }, props), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Menu-CheckboxItem-Indicator" }, /* @__PURE__ */ React.createElement(Menu.CheckboxItemIndicator, null, /* @__PURE__ */ React.createElement(CheckIcon, null))), children);
}
Menu$1.CheckboxItem = MenuCheckboxItem;
function MenuGroupLabel(_ref8) {
	let { className } = _ref8, props = _objectWithoutProperties(_ref8, _excluded6);
	const cn = `DocSearch-Menu-GroupLabel${className ? ` ${className}` : ""}`;
	return /* @__PURE__ */ React.createElement(Menu.GroupLabel, _objectSpread2({ className: cn }, props));
}
Menu$1.GroupLabel = MenuGroupLabel;

//#endregion
//#region src/components/FacetBar.tsx
const EMPTY_SELECTION$1 = [];
const FacetValueItem = React.memo(function FacetValueItem({ facetKey, value, checked, onToggle }) {
	const handleCheckedChange = React.useCallback(() => {
		onToggle(facetKey, value);
	}, [
		facetKey,
		onToggle,
		value
	]);
	return /* @__PURE__ */ React.createElement(Menu$1.CheckboxItem, {
		checked,
		label: value,
		onCheckedChange: handleCheckedChange
	}, capitalize(value));
});
function toggleValue(value) {
	return (currentValues) => currentValues.includes(value) ? currentValues.filter((selected) => selected !== value) : [...currentValues, value];
}
function removeValue(value) {
	return (currentValues) => currentValues.filter((selected) => selected !== value);
}
const FacetMenu = React.memo(function FacetMenu({ facet, selectedValues, defaultValueLabel, onSelectionChange, registerTrigger, menuTriggerSelectedAriaLabel }) {
	const label = getFacetLabel(facet);
	const hasSelection = selectedValues.length > 0;
	const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);
	const handleToggleValue = React.useCallback((facetKey, value) => {
		onSelectionChange(facetKey, toggleValue(value));
	}, [onSelectionChange]);
	const handleSelectAll = React.useCallback(() => {
		onSelectionChange(facet.key, EMPTY_SELECTION$1);
	}, [facet.key, onSelectionChange]);
	const triggerRef = React.useCallback((el) => {
		registerTrigger(facet.key, el);
	}, [facet.key, registerTrigger]);
	return /* @__PURE__ */ React.createElement(Menu$1, null, /* @__PURE__ */ React.createElement(Menu$1.Trigger, {
		ref: triggerRef,
		"data-has-selection": hasSelection,
		"aria-label": hasSelection ? `${label}, ${selectedValues.join(", ")} ${menuTriggerSelectedAriaLabel}` : label
	}, /* @__PURE__ */ React.createElement("span", null, capitalize(label)), /* @__PURE__ */ React.createElement(ChevronIcon, null)), /* @__PURE__ */ React.createElement(Menu$1.Popup, null, /* @__PURE__ */ React.createElement(Menu$1.Item, {
		className: "DocSearch-Menu-ResetItem",
		label: `${defaultValueLabel} ${label}`,
		onClick: handleSelectAll
	}, defaultValueLabel), facet.values.map((value) => /* @__PURE__ */ React.createElement(FacetValueItem, {
		key: value,
		facetKey: facet.key,
		value,
		checked: selectedSet.has(value),
		onToggle: handleToggleValue
	}))));
});
const SelectedFacetChip = React.memo(function SelectedFacetChip({ facetKey, facetLabel, value, dismissAriaLabel, onDismiss }) {
	const handleDismissFacet = React.useCallback((e) => {
		onDismiss(facetKey, value, e);
	}, [
		facetKey,
		onDismiss,
		value
	]);
	return /* @__PURE__ */ React.createElement(Chip, null, capitalize(value), /* @__PURE__ */ React.createElement(Chip.Dismiss, {
		"aria-label": `${dismissAriaLabel} ${capitalize(value)} (${facetLabel})`,
		onClick: handleDismissFacet
	}));
});
const FacetBar = React.memo(function FacetBar({ facets, selections, onSelectionChange, clearSelections, translations = {} }) {
	const { defaultValueLabel = "All", facetMenuTriggerAriaLabel = "selected", clearAllLabel = "Clear all", facetsAriaLabel = "Search filters", selectedFacetsAriaLabel = "Selected search filters", clearFacetAriaLabel = "Clear filter:" } = translations;
	const visibleFacetKeys = React.useMemo(() => new Set(facets.map((f) => f.key)), [facets]);
	const selectionsToDisplay = React.useMemo(() => {
		const entries = [];
		for (const [key, values] of Object.entries(selections)) {
			if (!visibleFacetKeys.has(key)) continue;
			for (const value of values) entries.push([key, value]);
		}
		return entries;
	}, [selections, visibleFacetKeys]);
	const triggerRefs = React.useRef(/* @__PURE__ */ new Map());
	const registerTrigger = React.useCallback((facetKey, el) => {
		if (el) triggerRefs.current.set(facetKey, el);
		else triggerRefs.current.delete(facetKey);
	}, []);
	const handleDismissFacet = React.useCallback((facetKey, value, ev) => {
		const dismissButton = ev.currentTarget;
		const selectionBar = dismissButton.closest(".DocSearch-FacetSelectionBar");
		if (selectionBar) {
			var _ref, _dismissButtons;
			const dismissButtons = Array.from(selectionBar.querySelectorAll(".DocSearch-Chip-Dismiss"));
			const index = dismissButtons.indexOf(dismissButton);
			const target = (_ref = (_dismissButtons = dismissButtons[index + 1]) !== null && _dismissButtons !== void 0 ? _dismissButtons : dismissButtons[index - 1]) !== null && _ref !== void 0 ? _ref : triggerRefs.current.get(facetKey);
			target === null || target === void 0 || target.focus();
		}
		onSelectionChange(facetKey, removeValue(value));
	}, [onSelectionChange]);
	const handleClearAll = React.useCallback(() => {
		var _triggerRefs$current$;
		(_triggerRefs$current$ = triggerRefs.current.values().next().value) === null || _triggerRefs$current$ === void 0 || _triggerRefs$current$.focus();
		clearSelections();
	}, [clearSelections]);
	const facetLabels = React.useMemo(() => new Map(facets.map((f) => [f.key, getFacetLabel(f)])), [facets]);
	if (facets.length === 0) return null;
	return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", {
		className: "DocSearch-FacetBar",
		role: "group",
		"aria-label": facetsAriaLabel
	}, facets.map((facet) => {
		var _selections$facet$key;
		return /* @__PURE__ */ React.createElement(FacetMenu, {
			key: facet.key,
			facet,
			selectedValues: (_selections$facet$key = selections[facet.key]) !== null && _selections$facet$key !== void 0 ? _selections$facet$key : EMPTY_SELECTION$1,
			defaultValueLabel,
			registerTrigger,
			menuTriggerSelectedAriaLabel: facetMenuTriggerAriaLabel,
			onSelectionChange
		});
	})), selectionsToDisplay.length > 0 && /* @__PURE__ */ React.createElement("div", {
		className: "DocSearch-FacetSelectionBar",
		role: "group",
		"aria-label": selectedFacetsAriaLabel
	}, selectionsToDisplay.map(([key, value]) => {
		var _facetLabels$get;
		return /* @__PURE__ */ React.createElement(SelectedFacetChip, {
			key: `${key}:${value}`,
			facetKey: key,
			facetLabel: (_facetLabels$get = facetLabels.get(key)) !== null && _facetLabels$get !== void 0 ? _facetLabels$get : key,
			value,
			dismissAriaLabel: clearFacetAriaLabel,
			onDismiss: handleDismissFacet
		});
	}), /* @__PURE__ */ React.createElement("button", {
		type: "button",
		className: "DocSearch-FacetSelectionBar-Action",
		onClick: handleClearAll
	}, clearAllLabel)));
});

//#endregion
//#region src/components/ui/SearchBoxForm.tsx
const _excluded$4 = [
	"actionsBeforeClose",
	"autoFocus",
	"clearButtonAriaLabel",
	"clearButtonTitle",
	"closeButtonAriaLabel",
	"closeButtonText",
	"hideInput",
	"inputOverlay",
	"inputProps",
	"inputRef",
	"isFromSelection",
	"leadingElement",
	"onClose",
	"placeholder",
	"searchInputLabel",
	"state"
];
function SearchBoxForm(_ref) {
	let { actionsBeforeClose, autoFocus, clearButtonAriaLabel, clearButtonTitle, closeButtonAriaLabel, closeButtonText, hideInput, inputOverlay, inputProps, inputRef, isFromSelection, leadingElement, onClose, placeholder, searchInputLabel, state } = _ref, autocomplete = _objectWithoutProperties(_ref, _excluded$4);
	const { onReset } = autocomplete.getFormProps({ inputElement: inputRef.current });
	React.useEffect(() => {
		if (autoFocus && inputRef.current) inputRef.current.focus();
	}, [autoFocus, inputRef]);
	React.useEffect(() => {
		if (isFromSelection && inputRef.current) inputRef.current.select();
	}, [isFromSelection, inputRef]);
	const baseInputProps = autocomplete.getInputProps({
		inputElement: inputRef.current,
		autoFocus,
		maxLength: 512
	});
	const isKeywordSearchLoading = state.status === "stalled";
	return /* @__PURE__ */ React.createElement("form", {
		className: "DocSearch-Form",
		onSubmit: (event) => {
			event.preventDefault();
		},
		onReset
	}, leadingElement || (isKeywordSearchLoading ? /* @__PURE__ */ React.createElement("div", { className: "DocSearch-LoadingIndicator" }, /* @__PURE__ */ React.createElement(LoadingIcon, null)) : /* @__PURE__ */ React.createElement("label", _objectSpread2({ className: "DocSearch-MagnifierLabel" }, autocomplete.getLabelProps()), /* @__PURE__ */ React.createElement(SearchIcon, null), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-VisuallyHiddenForAccessibility" }, searchInputLabel))), inputOverlay, /* @__PURE__ */ React.createElement("input", _objectSpread2(_objectSpread2(_objectSpread2({
		className: "DocSearch-Input",
		ref: inputRef
	}, baseInputProps), inputProps), {}, {
		placeholder,
		hidden: hideInput
	})), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Actions" }, actionsBeforeClose, /* @__PURE__ */ React.createElement("button", {
		type: "button",
		title: closeButtonText,
		className: "DocSearch-Action DocSearch-Close",
		"aria-label": closeButtonAriaLabel,
		onClick: onClose
	}, /* @__PURE__ */ React.createElement(CloseIcon, null))));
}

//#endregion
//#region src/components/ui/ModalShell.tsx
function ModalShell({ state, containerRef, modalRef, formElementRef, dropdownRef, getRootProps, onClose, showDropdown, searchBox, filterBar, screenState, footer }) {
	return /* @__PURE__ */ React.createElement("div", _objectSpread2(_objectSpread2({ ref: containerRef }, getRootProps({ "aria-expanded": true })), {}, {
		className: [
			"DocSearch",
			"DocSearch-Container",
			state.status === "stalled" && "DocSearch-Container--Stalled",
			state.status === "error" && "DocSearch-Container--Errored"
		].filter(Boolean).join(" "),
		role: "button",
		tabIndex: 0,
		onMouseDown: (event) => {
			if (event.target === event.currentTarget) onClose();
		}
	}), /* @__PURE__ */ React.createElement("div", {
		className: "DocSearch-Modal",
		ref: modalRef
	}, /* @__PURE__ */ React.createElement("header", {
		className: "DocSearch-SearchBar",
		ref: formElementRef
	}, searchBox), filterBar, showDropdown && /* @__PURE__ */ React.createElement("div", {
		className: "DocSearch-Dropdown",
		ref: dropdownRef
	}, screenState), /* @__PURE__ */ React.createElement("footer", { className: "DocSearch-Footer" }, footer)));
}

//#endregion
//#region src/Footer.tsx
function CommandIcon(props) {
	return /* @__PURE__ */ React.createElement("svg", {
		width: "20",
		height: "20",
		"aria-label": props.ariaLabel,
		viewBox: "0 0 24 24",
		role: "img"
	}, /* @__PURE__ */ React.createElement("g", {
		fill: "none",
		stroke: "currentColor",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		strokeWidth: "1.4"
	}, props.children));
}
function Footer({ translations = {}, isAskAiActive = false, footerAction }) {
	const { selectText = "Select", selectKeyAriaLabel = "Enter key", submitQuestionText = "Submit question", navigateText = "Navigate", navigateUpKeyAriaLabel = "Arrow up", navigateDownKeyAriaLabel = "Arrow down", closeText = "Close", backToSearchText = "Back to search", closeKeyAriaLabel = "Escape key", poweredByText = "Powered by" } = translations;
	const hasFooterAction = footerAction !== null && footerAction !== void 0 && typeof footerAction !== "boolean";
	return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("ul", { className: "DocSearch-Commands" }, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("kbd", { className: "DocSearch-Commands-Key" }, /* @__PURE__ */ React.createElement(CommandIcon, { ariaLabel: navigateDownKeyAriaLabel }, /* @__PURE__ */ React.createElement("path", { d: "M12 5v14" }), /* @__PURE__ */ React.createElement("path", { d: "m19 12-7 7-7-7" }))), /* @__PURE__ */ React.createElement("kbd", { className: "DocSearch-Commands-Key" }, /* @__PURE__ */ React.createElement(CommandIcon, { ariaLabel: navigateUpKeyAriaLabel }, /* @__PURE__ */ React.createElement("path", { d: "m5 12 7-7 7 7" }), /* @__PURE__ */ React.createElement("path", { d: "M12 19V5" }))), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Label" }, navigateText)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("kbd", { className: "DocSearch-Commands-Key" }, /* @__PURE__ */ React.createElement(CommandIcon, { ariaLabel: selectKeyAriaLabel }, /* @__PURE__ */ React.createElement("polyline", { points: "9 10 4 15 9 20" }), /* @__PURE__ */ React.createElement("path", { d: "M20 4v7a4 4 0 0 1-4 4H4" }))), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Label" }, isAskAiActive ? submitQuestionText : selectText)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("kbd", { className: "DocSearch-Commands-Key" }, /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Escape-Key" }, "ESC")), /* @__PURE__ */ React.createElement("span", {
		className: "DocSearch-Label",
		"aria-label": closeKeyAriaLabel
	}, isAskAiActive ? backToSearchText : closeText))), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Footer-Actions" }, hasFooterAction && /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Footer-Action" }, footerAction), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Logo" }, /* @__PURE__ */ React.createElement(AlgoliaLogo, { translations: { poweredByText } }))));
}

//#endregion
//#region src/Hit.tsx
function Hit({ hit, children }) {
	return /* @__PURE__ */ React.createElement("a", { href: hit.url }, children);
}

//#endregion
//#region src/useFacetValues.ts
function useFacetValues({ facets, indexes, searchClient }) {
	const [facetValues, setFacetValues] = React.useState({});
	const stableFacetKeys = facets.map((facet) => facet.key).join(",");
	const stableIndexes = JSON.stringify(indexes.map((index) => {
		var _index$searchParamete;
		return [index.name, (_index$searchParamete = index.searchParameters) !== null && _index$searchParamete !== void 0 ? _index$searchParamete : null];
	}));
	React.useEffect(() => {
		let isMounted = true;
		const facetKeys = stableFacetKeys ? stableFacetKeys.split(",") : [];
		if (facetKeys.length === 0 || indexes.length === 0) return () => {
			isMounted = false;
		};
		searchClient.search({ requests: indexes.map((index) => ({
			indexName: index.name,
			query: "",
			hitsPerPage: 0,
			facets: facetKeys
		})) }).then(({ results }) => {
			if (!isMounted) return;
			const valuesByFacet = facetKeys.reduce((acc, facet) => {
				acc[facet] = [];
				return acc;
			}, {});
			results.forEach((res) => {
				var _result$facets;
				Object.entries((_result$facets = res.facets) !== null && _result$facets !== void 0 ? _result$facets : {}).forEach(([facet, values]) => {
					if (!valuesByFacet[facet]) return;
					valuesByFacet[facet] = Array.from(/* @__PURE__ */ new Set([...valuesByFacet[facet], ...Object.keys(values)])).sort((a, b) => a.localeCompare(b, void 0, { sensitivity: "base" }));
				});
			});
			setFacetValues(valuesByFacet);
		}).catch(() => {
			if (isMounted) setFacetValues({});
		});
		return () => {
			isMounted = false;
		};
	}, [
		stableFacetKeys,
		stableIndexes,
		searchClient
	]);
	return facetValues;
}

//#endregion
//#region src/hooks/useDocSearchFacets.ts
const EMPTY_SELECTION = [];
/**
* Compares two selections for a facet. A missing entry and an empty array are
* both "nothing selected" so clearing an untouched facet doesn't trigger a
* refresh.
*/
function areSelectionsEqual(a, b) {
	const left = a !== null && a !== void 0 ? a : EMPTY_SELECTION;
	const right = b !== null && b !== void 0 ? b : EMPTY_SELECTION;
	if (left === right) return true;
	if (left.length !== right.length) return false;
	return left.every((value, index) => value === right[index]);
}
function useDocSearchFacets({ facets, indexes, searchClient, onSelectionsChange }) {
	const normalizedFacets = React.useMemo(() => normalizeFacets(facets), [facets]);
	const normalizedFacetsRef = React.useRef(normalizedFacets);
	const facetValues = useFacetValues({
		facets: normalizedFacets,
		indexes,
		searchClient
	});
	const [facetSelections, setFacetSelections] = React.useState(() => deriveDefaultSelectedFacetsFromIndex(indexes));
	const facetSelectionsRef = React.useRef(facetSelections);
	const onSelectionsChangeRef = React.useRef(onSelectionsChange);
	const visibleFacets = React.useMemo(() => normalizedFacets.map((facet) => {
		var _facetValues$facet$ke;
		return _objectSpread2(_objectSpread2({}, facet), {}, { values: (_facetValues$facet$ke = facetValues[facet.key]) !== null && _facetValues$facet$ke !== void 0 ? _facetValues$facet$ke : [] });
	}).filter((facet) => facet.values.length > 0), [facetValues, normalizedFacets]);
	React.useLayoutEffect(() => {
		normalizedFacetsRef.current = normalizedFacets;
		onSelectionsChangeRef.current = onSelectionsChange;
	});
	const applySelections = React.useCallback((next) => {
		var _onSelectionsChangeRe;
		facetSelectionsRef.current = next;
		setFacetSelections(next);
		(_onSelectionsChangeRe = onSelectionsChangeRef.current) === null || _onSelectionsChangeRe === void 0 || _onSelectionsChangeRe.call(onSelectionsChangeRef);
	}, []);
	return {
		visibleFacets,
		facetSelections,
		facetSelectionsRef,
		handleFacetSelectionChange: React.useCallback((facet, update) => {
			const existing = facetSelectionsRef.current[facet];
			const values = typeof update === "function" ? update(existing !== null && existing !== void 0 ? existing : EMPTY_SELECTION) : update;
			if (areSelectionsEqual(existing, values)) return;
			applySelections(_objectSpread2(_objectSpread2({}, facetSelectionsRef.current), {}, { [facet]: values }));
		}, [applySelections]),
		clearFacetSelections: React.useCallback(() => {
			const current = facetSelectionsRef.current;
			const next = {};
			let hasSelectedValues = false;
			for (const facet of normalizedFacetsRef.current) {
				const values = current[facet.key];
				if (values === void 0) continue;
				if (values.length > 0) hasSelectedValues = true;
				next[facet.key] = EMPTY_SELECTION;
			}
			if (!hasSelectedValues) return;
			applySelections(next);
		}, [applySelections])
	};
}

//#endregion
//#region src/hooks/useDocSearchInsights.ts
function useSendItemClickEvent(state) {
	return React.useCallback((item) => {
		if (!state.context.algoliaInsightsPlugin || !item.__autocomplete_id) return;
		const insightsItem = item;
		state.context.algoliaInsightsPlugin.insights.clickedObjectIDsAfterSearch({
			eventName: "Item Selected",
			index: insightsItem.__autocomplete_indexName,
			items: [insightsItem],
			positions: [item.__autocomplete_id],
			queryID: insightsItem.__autocomplete_queryID
		});
	}, [state.context.algoliaInsightsPlugin]);
}

//#endregion
//#region src/hooks/useInitialModalQuery.ts
function useInitialModalQuery(initialQueryFromProp) {
	const initialQueryFromSelection = React.useRef(typeof window !== "undefined" ? window.getSelection().toString().slice(0, 512) : "").current;
	return {
		initialQuery: React.useRef(initialQueryFromProp || initialQueryFromSelection).current,
		initialQueryFromSelection
	};
}

//#endregion
//#region src/useTouchEvents.ts
function useTouchEvents({ getEnvironmentProps, panelElement, formElement, inputElement }) {
	React.useEffect(() => {
		if (!(panelElement && formElement && inputElement)) return;
		const { onTouchStart, onTouchMove } = getEnvironmentProps({
			panelElement,
			formElement,
			inputElement
		});
		window.addEventListener("touchstart", onTouchStart);
		window.addEventListener("touchmove", onTouchMove);
		return () => {
			window.removeEventListener("touchstart", onTouchStart);
			window.removeEventListener("touchmove", onTouchMove);
		};
	}, [
		getEnvironmentProps,
		panelElement,
		formElement,
		inputElement
	]);
}

//#endregion
//#region src/useTrapFocus.ts
function useTrapFocus({ container }) {
	React.useEffect(() => {
		if (!container) return;
		const focusableElements = container.querySelectorAll("a[href]:not([disabled]), button:not([disabled]), input:not([disabled])");
		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];
		function trapFocus(event) {
			if (event.key !== "Tab") return;
			if (event.shiftKey) {
				if (document.activeElement === firstElement) {
					event.preventDefault();
					lastElement.focus();
				}
			} else if (document.activeElement === lastElement) {
				event.preventDefault();
				firstElement.focus();
			}
		}
		container.addEventListener("keydown", trapFocus);
		return () => {
			container.removeEventListener("keydown", trapFocus);
		};
	}, [container]);
}

//#endregion
//#region src/hooks/useDocSearchModalEffects.ts
function useDocSearchModalEffects({ initialScrollY, modalRef, theme }) {
	useTheme({ theme });
	React.useEffect(() => {
		document.body.classList.add("DocSearch--active");
		return () => {
			var _window$scrollTo, _window;
			document.body.classList.remove("DocSearch--active");
			(_window$scrollTo = (_window = window).scrollTo) === null || _window$scrollTo === void 0 || _window$scrollTo.call(_window, 0, initialScrollY);
		};
	}, [initialScrollY]);
	React.useEffect(() => {
		manageLocalStorageQuota();
	}, []);
	React.useLayoutEffect(() => {
		const scrollBarWidth = window.innerWidth - document.body.clientWidth;
		document.body.style.marginInlineEnd = `${scrollBarWidth}px`;
		return () => {
			document.body.style.marginInlineEnd = "0px";
		};
	}, []);
	React.useEffect(() => {
		function setFullViewportHeight() {
			if (modalRef.current) {
				const vh = window.innerHeight * .01;
				modalRef.current.style.setProperty("--docsearch-vh", `${vh}px`);
			}
		}
		setFullViewportHeight();
		window.addEventListener("resize", setFullViewportHeight);
		return () => {
			window.removeEventListener("resize", setFullViewportHeight);
		};
	}, [modalRef]);
}

//#endregion
//#region src/hooks/useModalEnvironment.ts
function useModalEnvironment({ getEnvironmentProps, containerRef, dropdownRef, formElementRef, inputRef, initialScrollY, modalRef, theme }) {
	useTouchEvents({
		getEnvironmentProps,
		panelElement: dropdownRef.current,
		formElement: formElementRef.current,
		inputElement: inputRef.current
	});
	useTrapFocus({ container: containerRef.current });
	useDocSearchModalEffects({
		initialScrollY,
		modalRef,
		theme
	});
}

//#endregion
//#region src/hooks/useModalRefs.ts
function useModalRefs() {
	return {
		containerRef: React.useRef(null),
		modalRef: React.useRef(null),
		formElementRef: React.useRef(null),
		dropdownRef: React.useRef(null),
		inputRef: React.useRef(null)
	};
}

//#endregion
//#region src/hooks/useRefreshOnInitialQuery.ts
function useRefreshOnInitialQuery({ initialQuery, inputRef, refresh }) {
	React.useEffect(() => {
		if (initialQuery.length > 0) {
			refresh();
			if (inputRef.current) inputRef.current.focus();
		}
	}, [
		initialQuery,
		inputRef,
		refresh
	]);
}

//#endregion
//#region src/hooks/useSaveRecentSearch.ts
function createSyntheticParent(item) {
	const hierarchy = item.hierarchy;
	const deepestLevel = [
		"lvl6",
		"lvl5",
		"lvl4",
		"lvl3",
		"lvl2",
		"lvl1",
		"lvl0"
	].find((level) => hierarchy[level]);
	return _objectSpread2(_objectSpread2({}, item), {}, {
		type: deepestLevel || "lvl0",
		content: null
	});
}
function useSaveRecentSearch({ favoriteSearches, recentSearches, disableUserPersonalization }) {
	return React.useCallback(function saveRecentSearch(item) {
		if (disableUserPersonalization) return;
		const search = item.type === "content" ? item.__docsearch_parent || createSyntheticParent(item) : item;
		if (search && favoriteSearches.getAll().findIndex((x) => x.objectID === search.objectID) === -1) recentSearches.add(search);
	}, [
		favoriteSearches,
		recentSearches,
		disableUserPersonalization
	]);
}

//#endregion
//#region src/hooks/useStoredDocSearches.ts
function useStoredDocSearches({ defaultIndexName, recentSearchesLimit, recentSearchesWithFavoritesLimit }) {
	const favoriteSearches = React.useRef(createStoredSearches({
		key: `__DOCSEARCH_FAVORITE_SEARCHES__${defaultIndexName}`,
		limit: 10
	})).current;
	return {
		favoriteSearches,
		recentSearches: React.useRef(createStoredSearches({
			key: `__DOCSEARCH_RECENT_SEARCHES__${defaultIndexName}`,
			limit: favoriteSearches.getAll().length === 0 ? recentSearchesLimit : recentSearchesWithFavoritesLimit
		})).current
	};
}

//#endregion
//#region src/components/ui/HitContent.tsx
function HitContent({ title, subText }) {
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-content-wrapper" }, /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Hit-title" }, title), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Hit-path" }, subText));
}

//#endregion
//#region src/hooks/useRelativeFormattedDate.ts
const MS_IN_SECOND = 1e3;
const MS_IN_MINUTE = MS_IN_SECOND * 60;
const MS_IN_HOUR = MS_IN_MINUTE * 60;
const UNITS = [
	[MS_IN_HOUR * 24, "day"],
	[MS_IN_HOUR, "hour"],
	[MS_IN_MINUTE, "minute"],
	[MS_IN_SECOND, "second"]
];
function useRelativeFormattedDate(start, end = Date.now()) {
	const [diff] = useState(() => {
		var _UNITS$find;
		const elapsed = Math.abs(Number(end) - Number(start));
		const [ms, unit] = (_UNITS$find = UNITS.find(([threshold]) => elapsed >= threshold)) !== null && _UNITS$find !== void 0 ? _UNITS$find : UNITS[UNITS.length - 1];
		return [Math.floor(elapsed / ms), unit];
	});
	const formattedDate = useMemo(() => {
		const locale = typeof navigator !== "undefined" ? navigator.language : void 0;
		const [elapsedTime, elapsedUnit] = diff;
		return new Intl.RelativeTimeFormat(locale, { style: "long" }).format(elapsedTime * -1, elapsedUnit);
	}, [diff]);
	return start ? formattedDate : null;
}

//#endregion
//#region src/Snippet.tsx
const _excluded$3 = [
	"hit",
	"attribute",
	"tagName"
];
function getPropertyByPath(object, path) {
	return path.split(".").reduce((prev, current) => {
		if (prev === null || prev === void 0 ? void 0 : prev[current]) return prev[current];
		return null;
	}, object);
}
function Snippet(_ref) {
	let { hit, attribute, tagName = "span" } = _ref;
	return createElement(tagName, _objectSpread2(_objectSpread2({}, _objectWithoutProperties(_ref, _excluded$3)), {}, { dangerouslySetInnerHTML: { __html: getPropertyByPath(hit, `_snippetResult.${attribute}.value`) || getPropertyByPath(hit, attribute) } }));
}

//#endregion
//#region src/Results.tsx
function Results(props) {
	var _props$sourceIcon;
	const { askAiResultsTitle = "Ask AI Assistant" } = props.translations || {};
	const askAiResultsId = React.useId();
	const decodedTitle = React.useMemo(() => {
		if (!props.title) return null;
		return decodeHtmlEntities(props.title);
	}, [props.title]);
	if (!props.collection || props.collection.items.length === 0) return null;
	if (props.collection.source.sourceId === SOURCE_IDS.askAI) return /* @__PURE__ */ React.createElement("section", { className: "DocSearch-Hits" }, /* @__PURE__ */ React.createElement("h2", {
		id: askAiResultsId,
		className: "DocSearch-Hit-source"
	}, askAiResultsTitle), /* @__PURE__ */ React.createElement("ul", _objectSpread2(_objectSpread2({ className: "DocSearch-Hits-padded" }, props.getListProps({ source: props.collection.source })), {}, { "aria-labelledby": askAiResultsId }), props.collection.items.map((item) => /* @__PURE__ */ React.createElement(AskAiButton, _objectSpread2({
		key: item.objectID,
		item,
		translations: props.translations
	}, props)))));
	if (props.collection.source.sourceId === SOURCE_IDS.recentConversations) return /* @__PURE__ */ React.createElement("section", { className: "DocSearch-Hits" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-source" }, /* @__PURE__ */ React.createElement(SparklesIcon, null), decodedTitle), /* @__PURE__ */ React.createElement("ul", _objectSpread2({ className: "DocSearch-Hits-padded" }, props.getListProps({ source: props.collection.source })), props.collection.items.map((item, index) => {
		return /* @__PURE__ */ React.createElement(Result, _objectSpread2({
			key: [props.title, item.objectID].join(":"),
			item,
			index
		}, props));
	})));
	return /* @__PURE__ */ React.createElement("section", { className: "DocSearch-Hits" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-source" }, (_props$sourceIcon = props.sourceIcon) !== null && _props$sourceIcon !== void 0 ? _props$sourceIcon : null, decodedTitle), /* @__PURE__ */ React.createElement("ul", _objectSpread2({ className: "DocSearch-Hits-padded" }, props.getListProps({ source: props.collection.source })), props.collection.items.map((item, index) => {
		return /* @__PURE__ */ React.createElement(Result, _objectSpread2({
			key: [props.title, item.objectID].join(":"),
			item,
			index
		}, props));
	})));
}
function Result({ item, index, renderIcon, renderAction, getItemProps, onItemClick, collection, hitComponent, translations = {}, renderResultBadge }) {
	const Hit = hitComponent;
	const { recentConversationTimestampFallback = "A while ago" } = translations;
	const titleAttribute = item.type === "content" ? "content" : `hierarchy.${item.type}`;
	const breadcrumbs = getHitItemBreadcrumbs(item);
	return /* @__PURE__ */ React.createElement("li", _objectSpread2({ className: ["DocSearch-Hit", item.__docsearch_parent && "DocSearch-Hit--Child"].filter(Boolean).join(" ") }, getItemProps({
		item,
		source: collection.source,
		onClick(event) {
			onItemClick(item, event);
		}
	})), /* @__PURE__ */ React.createElement(Hit, { hit: item }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-Container" }, renderIcon({
		item,
		index
	}), item.type === "lvl0" && /* @__PURE__ */ React.createElement(HitContent, {
		title: /* @__PURE__ */ React.createElement(Snippet, {
			hit: item,
			attribute: "hierarchy.lvl0"
		}),
		subText: /* @__PURE__ */ React.createElement(Snippet, {
			hit: item,
			attribute: "content"
		})
	}), item.type === "askAI" ? /* @__PURE__ */ React.createElement(AskAIResultContent, {
		item,
		relativeDateFallbackText: recentConversationTimestampFallback
	}) : /* @__PURE__ */ React.createElement(HitContent, {
		title: /* @__PURE__ */ React.createElement(Snippet, {
			hit: item,
			attribute: titleAttribute
		}),
		subText: breadcrumbs
	}), renderResultBadge === null || renderResultBadge === void 0 ? void 0 : renderResultBadge({ item }), renderAction({ item }))));
}
function AskAIResultContent({ item, relativeDateFallbackText }) {
	const relativeDate = useRelativeFormattedDate(item.hierarchy.lvl2 ? new Date(item.hierarchy.lvl2) : null);
	return /* @__PURE__ */ React.createElement(HitContent, {
		title: decodeHtmlEntities(item.hierarchy.lvl1 || ""),
		subText: relativeDate || relativeDateFallbackText
	});
}
function AskAiButton({ item, getItemProps, onItemClick, collection }) {
	if (!item.query) return null;
	return /* @__PURE__ */ React.createElement("li", _objectSpread2({ className: "DocSearch-Hit" }, getItemProps({
		item,
		source: collection.source,
		onClick(event) {
			onItemClick(item, event);
		}
	})), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit--AskAI" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-AskAIButton DocSearch-Hit-Container" }, /* @__PURE__ */ React.createElement("div", { className: " DocSearch-Hit-AskAIButton-icon DocSearch-Hit-icon" }, /* @__PURE__ */ React.createElement(SparklesIcon, null)), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-AskAIButton-title" }, /* @__PURE__ */ React.createElement("span", { className: "DocSearch-Hit-AskAIButton-title-query" }, item.query)))));
}

//#endregion
//#region src/components/ui/StoredSearchesSections.tsx
const _excluded$2 = ["translations"];
function StoredSearchesSections(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$2);
	const { recentSearchesTitle = "Recently viewed docs", saveRecentSearchButtonTitle = "Pin this search", removeRecentSearchButtonTitle = "Remove this search from history", favoriteSearchesTitle = "Pinned", removeFavoriteSearchButtonTitle = "Remove this saved search" } = translations;
	const favoriteSearches = getCollection(props.state, "favoriteSearches");
	const recentSearchesCollection = getCollection(props.state, "recentSearches");
	return /* @__PURE__ */ React.createElement(React.Fragment, null, favoriteSearches && /* @__PURE__ */ React.createElement(Results, _objectSpread2(_objectSpread2({}, props), {}, {
		title: favoriteSearchesTitle,
		collection: favoriteSearches,
		sourceIcon: /* @__PURE__ */ React.createElement(PinIcon, null),
		renderIcon: ({ item }) => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-icon" }, /* @__PURE__ */ React.createElement(SourceIcon, { type: item.type })),
		renderAction: ({ item }) => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-action" }, /* @__PURE__ */ React.createElement("button", {
			className: "DocSearch-Hit-action-button",
			title: removeFavoriteSearchButtonTitle,
			type: "submit",
			onClick: (event) => {
				event.preventDefault();
				event.stopPropagation();
				props.favoriteSearches.remove(item);
				props.refresh();
			}
		}, /* @__PURE__ */ React.createElement(CloseIcon, null)))
	})), recentSearchesCollection && /* @__PURE__ */ React.createElement(Results, _objectSpread2(_objectSpread2({}, props), {}, {
		title: recentSearchesTitle,
		collection: recentSearchesCollection,
		sourceIcon: /* @__PURE__ */ React.createElement(RecentIcon, null),
		renderIcon: ({ item }) => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-icon" }, /* @__PURE__ */ React.createElement(SourceIcon, { type: item.type })),
		renderAction: ({ item }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-action" }, /* @__PURE__ */ React.createElement("button", {
			className: "DocSearch-Hit-action-button DocSearch-Hit-action-button--pin",
			title: saveRecentSearchButtonTitle,
			type: "submit",
			onClick: (event) => {
				event.preventDefault();
				event.stopPropagation();
				props.favoriteSearches.add(item);
				props.recentSearches.remove(item);
				props.refresh();
			}
		}, /* @__PURE__ */ React.createElement(PinIcon, null))), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-action" }, /* @__PURE__ */ React.createElement("button", {
			className: "DocSearch-Hit-action-button",
			title: removeRecentSearchButtonTitle,
			type: "submit",
			onClick: (event) => {
				event.preventDefault();
				event.stopPropagation();
				props.recentSearches.remove(item);
				props.refresh();
			}
		}, /* @__PURE__ */ React.createElement(CloseIcon, null))))
	})));
}

//#endregion
//#region src/ErrorScreen.tsx
function ErrorScreen({ translations = {} }) {
	const { titleText = "Unable to fetch results", helpText = "You might want to check your network connection." } = translations;
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-ErrorScreen" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Screen-Icon" }, /* @__PURE__ */ React.createElement(ErrorIcon, null)), /* @__PURE__ */ React.createElement("p", { className: "DocSearch-Title" }, titleText), /* @__PURE__ */ React.createElement("p", { className: "DocSearch-Help" }, helpText));
}

//#endregion
//#region src/NoResultsScreen.tsx
const _excluded$1 = ["translations"];
function NoResultsScreen(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$1);
	const { noResultsText = "No results found for", suggestedQueryText = "Try searching for", reportMissingResultsText = "Believe this query should return results?", reportMissingResultsLinkText = "Let us know." } = translations;
	const searchSuggestions = props.state.context.searchSuggestions;
	return /* @__PURE__ */ React.createElement("div", { className: `DocSearch-NoResults ${props.canHandleAskAi ? "DocSearch-NoResults--withAskAi" : ""}` }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Screen-Icon" }, /* @__PURE__ */ React.createElement(NoResultsIcon, null)), /* @__PURE__ */ React.createElement("p", { className: "DocSearch-Title" }, noResultsText, " \"", /* @__PURE__ */ React.createElement("strong", null, props.state.query), "\""), searchSuggestions && searchSuggestions.length > 0 && /* @__PURE__ */ React.createElement("div", { className: "DocSearch-NoResults-Prefill-List" }, /* @__PURE__ */ React.createElement("p", { className: "DocSearch-Help" }, suggestedQueryText, ":"), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-NoResults-Prefill-List-Items" }, searchSuggestions.slice(0, 3).reduce((acc, search) => [...acc, /* @__PURE__ */ React.createElement("p", { key: search }, /* @__PURE__ */ React.createElement(SearchIcon, { size: 16 }), /* @__PURE__ */ React.createElement("button", {
		className: "DocSearch-Prefill",
		key: search,
		type: "button",
		onClick: () => {
			props.setQuery(search.toLowerCase() + " ");
			props.refresh();
			props.inputRef.current.focus();
		}
	}, search))], []))), props.getMissingResultsUrl && /* @__PURE__ */ React.createElement("p", { className: "DocSearch-Help" }, `${reportMissingResultsText} `, /* @__PURE__ */ React.createElement("a", {
		href: props.getMissingResultsUrl({ query: props.state.query }),
		target: "_blank",
		rel: "noopener noreferrer"
	}, reportMissingResultsLinkText)));
}

//#endregion
//#region src/components/HitResultBadge.tsx
function isRenderablePrimitive(v) {
	return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}
function HitResultBadge({ item, resultBadgeKey, translations = {} }) {
	if (!resultBadgeKey) return null;
	const parsedValue = getNestedValue(item, resultBadgeKey);
	if (parsedValue === null || typeof parsedValue === "undefined") return null;
	const { resultBadgeLabelText = "Category" } = translations;
	let badgeValue = null;
	if (isRenderablePrimitive(parsedValue)) badgeValue = String(parsedValue).trim();
	else if (Array.isArray(parsedValue)) {
		const parts = parsedValue.filter(isRenderablePrimitive).map((v) => String(v).trim());
		badgeValue = parts.length > 0 ? parts.join(", ") : null;
	}
	if (badgeValue === null || badgeValue.length === 0) return null;
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-badge" }, /* @__PURE__ */ React.createElement("span", { className: "DocSearch-VisuallyHiddenForAccessibility" }, `${resultBadgeLabelText}: ${badgeValue}`), /* @__PURE__ */ React.createElement("span", { "aria-hidden": "true" }, badgeValue));
}

//#endregion
//#region src/ResultsScreen.tsx
const _excluded = ["translations", "resultBadgeKey"];
function ResultsScreen(_ref) {
	let { translations = {}, resultBadgeKey } = _ref, props = _objectWithoutProperties(_ref, _excluded);
	const renderAction = React.useCallback(() => {
		return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-action" }, /* @__PURE__ */ React.createElement(SelectIcon, null));
	}, []);
	const renderResultBadge = React.useCallback(({ item }) => {
		return /* @__PURE__ */ React.createElement(HitResultBadge, {
			item,
			resultBadgeKey,
			translations: { resultBadgeLabelText: translations.resultBadgeLabelText }
		});
	}, [resultBadgeKey, translations.resultBadgeLabelText]);
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Dropdown-Container" }, props.state.collections.map((collection) => {
		if (collection.items.length === 0) return null;
		const title = removeHighlightTags(collection.items[0]);
		return /* @__PURE__ */ React.createElement(Results, _objectSpread2(_objectSpread2({}, props), {}, {
			key: collection.source.sourceId,
			translations,
			title,
			collection,
			renderIcon: ({ item, index }) => {
				var _collection$items;
				return /* @__PURE__ */ React.createElement(React.Fragment, null, item.__docsearch_parent && /* @__PURE__ */ React.createElement("svg", {
					className: "DocSearch-Hit-Tree",
					viewBox: "0 0 24 54"
				}, /* @__PURE__ */ React.createElement("g", {
					stroke: "currentColor",
					fill: "none",
					fillRule: "evenodd",
					strokeLinecap: "round",
					strokeLinejoin: "round"
				}, item.__docsearch_parent !== ((_collection$items = collection.items[index + 1]) === null || _collection$items === void 0 ? void 0 : _collection$items.__docsearch_parent) ? /* @__PURE__ */ React.createElement("path", { d: "M8 6v21M20 27H8.3" }) : /* @__PURE__ */ React.createElement("path", { d: "M8 6v42M20 27H8.3" }))), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-icon" }, /* @__PURE__ */ React.createElement(SourceIcon, { type: item.type })));
			},
			renderAction,
			renderResultBadge
		}));
	}), props.resultsFooterComponent && /* @__PURE__ */ React.createElement("section", { className: "DocSearch-HitsFooter" }, /* @__PURE__ */ React.createElement(props.resultsFooterComponent, { state: props.state })));
}

//#endregion
//#region src/utils/createDocSearchSources.ts
function isFacetFilterOverridden(facetFilter, selectedFacets) {
	if (typeof facetFilter === "string") {
		const key = getFacetFilterKey(facetFilter);
		return key !== null && selectedFacets.has(key);
	}
	return facetFilter.length > 0 && facetFilter.every((entry) => isFacetFilterOverridden(entry, selectedFacets));
}
/**
* Merges configured `facetFilters` with the user's facet selections.
*
* Multiple values selected for the same facet are combined into an OR group
* (nested array). Different facets are combined with AND (top-level entries),
* following Algolia `facetFilters` semantics.
*/
function createFacetFilters(searchParametersFacetFilters, facetSelections) {
	const selections = Object.entries(facetSelections);
	if (selections.length === 0) return searchParametersFacetFilters;
	const selectedFacets = new Set(selections.map(([facet]) => facet));
	const dynamicFacetFilters = [];
	for (const [facet, values] of selections) if (values.length === 1) dynamicFacetFilters.push(`${facet}:${values[0]}`);
	else if (values.length > 1) dynamicFacetFilters.push(values.map((value) => `${facet}:${value}`));
	let configuredFacetFilters = [];
	if (searchParametersFacetFilters && Array.isArray(searchParametersFacetFilters)) configuredFacetFilters = searchParametersFacetFilters;
	else if (searchParametersFacetFilters) configuredFacetFilters = [searchParametersFacetFilters];
	return [...configuredFacetFilters.filter((facetFilter) => !isFacetFilterOverridden(facetFilter, selectedFacets)), ...dynamicFacetFilters];
}
function buildNoQuerySources({ recentSearches, favoriteSearches, saveRecentSearch, onClose, disableUserPersonalization }) {
	if (disableUserPersonalization) return [];
	return [{
		sourceId: SOURCE_IDS.favoriteSearches,
		onSelect({ item, event }) {
			saveRecentSearch(item);
			if (!isModifierEvent(event)) onClose();
		},
		getItemUrl({ item }) {
			return item.url;
		},
		getItems() {
			return favoriteSearches.getAll();
		}
	}, {
		sourceId: SOURCE_IDS.recentSearches,
		onSelect({ item, event }) {
			saveRecentSearch(item);
			if (!isModifierEvent(event)) onClose();
		},
		getItemUrl({ item }) {
			return item.url;
		},
		getItems() {
			return recentSearches.getAll();
		}
	}];
}
async function buildQuerySources({ query, state: sourcesState, setContext, setStatus, searchClient, indexes, insights, appId, apiKey, maxResultsPerGroup, transformItems = identity, saveRecentSearch, onClose, facetSelections }) {
	const insightsActive = insights;
	try {
		const { results } = await searchClient.search({ requests: indexes.map((index) => {
			var _searchParams$attribu, _searchParams$attribu2, _searchParams$snippet, _searchParams$highlig, _searchParams$highlig2, _searchParams$hitsPer, _searchParams$clickAn;
			const indexName = index.name;
			const searchParams = index.searchParameters;
			const facetFilters = createFacetFilters(searchParams === null || searchParams === void 0 ? void 0 : searchParams.facetFilters, facetSelections.current);
			return _objectSpread2(_objectSpread2({
				query,
				indexName,
				attributesToRetrieve: (_searchParams$attribu = searchParams === null || searchParams === void 0 ? void 0 : searchParams.attributesToRetrieve) !== null && _searchParams$attribu !== void 0 ? _searchParams$attribu : [
					"hierarchy.lvl0",
					"hierarchy.lvl1",
					"hierarchy.lvl2",
					"hierarchy.lvl3",
					"hierarchy.lvl4",
					"hierarchy.lvl5",
					"hierarchy.lvl6",
					"content",
					"type",
					"url"
				],
				attributesToSnippet: (_searchParams$attribu2 = searchParams === null || searchParams === void 0 ? void 0 : searchParams.attributesToSnippet) !== null && _searchParams$attribu2 !== void 0 ? _searchParams$attribu2 : [
					`hierarchy.lvl1:${15}`,
					`hierarchy.lvl2:${15}`,
					`hierarchy.lvl3:${15}`,
					`hierarchy.lvl4:${15}`,
					`hierarchy.lvl5:${15}`,
					`hierarchy.lvl6:${15}`,
					`content:${15}`
				],
				snippetEllipsisText: (_searchParams$snippet = searchParams === null || searchParams === void 0 ? void 0 : searchParams.snippetEllipsisText) !== null && _searchParams$snippet !== void 0 ? _searchParams$snippet : "…",
				highlightPreTag: (_searchParams$highlig = searchParams === null || searchParams === void 0 ? void 0 : searchParams.highlightPreTag) !== null && _searchParams$highlig !== void 0 ? _searchParams$highlig : "<mark>",
				highlightPostTag: (_searchParams$highlig2 = searchParams === null || searchParams === void 0 ? void 0 : searchParams.highlightPostTag) !== null && _searchParams$highlig2 !== void 0 ? _searchParams$highlig2 : "</mark>",
				hitsPerPage: (_searchParams$hitsPer = searchParams === null || searchParams === void 0 ? void 0 : searchParams.hitsPerPage) !== null && _searchParams$hitsPer !== void 0 ? _searchParams$hitsPer : 20,
				clickAnalytics: (_searchParams$clickAn = searchParams === null || searchParams === void 0 ? void 0 : searchParams.clickAnalytics) !== null && _searchParams$clickAn !== void 0 ? _searchParams$clickAn : insightsActive
			}, searchParams), facetFilters ? { facetFilters } : {});
		}) });
		return results.flatMap((res) => {
			const result = res;
			const { hits, nbHits } = result;
			const sources = groupBy(transformItems(hits), (hit) => removeHighlightTags(hit), maxResultsPerGroup);
			if (sourcesState.context.searchSuggestions.length < Object.keys(sources).length) {
				var _sourcesState$context;
				setContext({ searchSuggestions: _objectSpread2(_objectSpread2({}, (_sourcesState$context = sourcesState.context.searchSuggestions) !== null && _sourcesState$context !== void 0 ? _sourcesState$context : []), Object.keys(sources)) });
			}
			if (nbHits) {
				const currentNbHits = sourcesState.context.nbHits;
				setContext({ nbHits: (currentNbHits !== null && currentNbHits !== void 0 ? currentNbHits : 0) + nbHits });
			}
			let insightsParams = {};
			if (insightsActive) insightsParams = {
				__autocomplete_indexName: result.index,
				__autocomplete_queryID: result.queryID,
				__autocomplete_algoliaCredentials: {
					appId,
					apiKey
				}
			};
			return Object.values(sources).map((items, index) => ({
				sourceId: `hits_${result.index}_${index}`,
				onSelect({ item, event }) {
					saveRecentSearch(item);
					if (!isModifierEvent(event)) onClose();
				},
				getItemUrl({ item }) {
					return item.url;
				},
				getItems() {
					return Object.values(groupBy(items, (item) => item.hierarchy.lvl1, maxResultsPerGroup)).map((groupedHits) => groupedHits.map((item) => {
						let parent = null;
						const potentialParent = groupedHits.find((siblingItem) => siblingItem.type === "lvl1" && siblingItem.hierarchy.lvl1 === item.hierarchy.lvl1);
						if (item.type !== "lvl1" && potentialParent) parent = potentialParent;
						return _objectSpread2(_objectSpread2({}, item), {}, { __docsearch_parent: parent }, insightsParams);
					})).flat();
				}
			}));
		});
	} catch (error) {
		if (error.name === "RetryError") setStatus("error");
		throw error;
	}
}

//#endregion
//#region src/utils/normalizeDocSearchIndexes.ts
function normalizeDocSearchIndexes({ indices }) {
	if (!Array.isArray(indices) || indices.length < 1) throw new Error("Must supply at least one `indices` entry for DocSearch");
	return indices.map((index) => typeof index === "string" ? { name: index } : index);
}

//#endregion
export { noop as C, SOURCE_IDS as D, identity as E, getCollection as O, scrollTo as S, isModifierEvent as T, Hit as _, NoResultsScreen as a, SearchBoxForm as b, Results as c, useRefreshOnInitialQuery as d, useModalRefs as f, useDocSearchFacets as g, useSendItemClickEvent as h, ResultsScreen as i, useStoredDocSearches as l, useInitialModalQuery as m, buildNoQuerySources as n, ErrorScreen as o, useModalEnvironment as p, buildQuerySources as r, StoredSearchesSections as s, normalizeDocSearchIndexes as t, useSaveRecentSearch as u, Footer as v, isQueryEmpty as w, FacetBar as x, ModalShell as y };
//# sourceMappingURL=normalizeDocSearchIndexes-B_bI_ytC.js.map